<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
    CalendarIcon,
    ChevronRightIcon,
    ExternalLinkIcon,
    PhoneIcon,
    UsersIcon,
} from '@heroicons/vue/solid';
import { MetaIntegrationService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import { MetaWaba, MetaWhatsAppNumberPolicy, WhatsAppWabaStatusColorMap } from '@/types';
import { formatDateAsString } from '@/utils/datetime';
import { attendanceUrl } from '@/configs';
import { operatingPhoneCount, wabaStatus } from '@/utils/whatsappMeta';
import CompanyPhoneQuotaSummary from './components/CompanyPhoneQuotaSummary.vue';
import WhatsAppMetaHeader from './components/WhatsAppMetaHeader.vue';

const router = useRouter();
const { t } = useI18n();
const wabas = ref<MetaWaba[]>([]);
const policy = ref<MetaWhatsAppNumberPolicy | null>(null);
const currentPage = ref(1);
const perPage = 10;

const paginatedWabas = computed(() => {
    const start = (currentPage.value - 1) * perPage;
    return wabas.value.slice(start, start + perPage);
});

const openWaba = (wabaId: string) => {
    router.push({ name: 'WhatsAppMetaDetail', params: { wabaId } });
};

const loadPolicy = async () => {
    try {
        policy.value = (await MetaIntegrationService.getWhatsAppNumberPolicy()).data;
    } catch {
        policy.value = null;
    }
};

const load = async () => {
    loadPolicy();
    try {
        const { data } = await MetaIntegrationService.getWabas();
        wabas.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

onMounted(load);
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <div class="flex flex-wrap items-center justify-between gap-3">
            <UiButton
                variant="white"
                text-variant="primary"
                @click="router.push({ name: 'Integration' })"
            >
                {{ t('whatsappMeta.list.back') }}
            </UiButton>
            <div class="flex flex-wrap gap-3">
                <a
                    v-if="attendanceUrl"
                    class="text-primary flex items-center justify-center rounded bg-white px-3 py-2 text-sm font-medium shadow ring-1 ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
                    :href="attendanceUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ t('whatsappMeta.list.openAttendance') }}
                    <ExternalLinkIcon class="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
                <UiButton
                    variant="primary"
                    text-variant="white"
                    @click="router.push({ name: 'ConnectWhatsAppMeta' })"
                >
                    {{ t('whatsappMeta.list.connect') }}
                </UiButton>
            </div>
        </div>

        <div class="space-y-6 rounded-lg bg-white p-4 shadow sm:p-6">
            <WhatsAppMetaHeader
                :title="t('whatsappMeta.list.title')"
                :subtitle="t('whatsappMeta.list.subtitle')"
                :summary="t('whatsappMeta.list.accountCount', wabas.length)"
            />

            <CompanyPhoneQuotaSummary
                v-if="wabas.length && policy"
                :used="policy.used"
                :limit="policy.limit"
                :full="policy.full"
            />

            <UiSettingsSection :title="t('whatsappMeta.list.accounts')">
                <UiSettingsRow
                    v-if="wabas.length === 0"
                    :title="t('whatsappMeta.list.emptyTitle')"
                    :description="t('whatsappMeta.list.empty')"
                />
                <button
                    v-for="waba in paginatedWabas"
                    :key="waba.wabaId"
                    type="button"
                    class="focus-visible:ring-primary flex min-h-[52px] w-full items-center gap-3 p-4 text-left hover:bg-gray-50/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                    @click="openWaba(waba.wabaId)"
                >
                    <span class="min-w-0 flex-1 space-y-1">
                        <span class="flex flex-wrap items-center gap-2">
                            <span class="truncate text-sm font-semibold text-gray-900">
                                {{ waba.displayName || waba.wabaId }}
                            </span>
                            <UiBadge
                                :variant="WhatsAppWabaStatusColorMap[wabaStatus(waba)]"
                                size="sm"
                            >
                                {{ t(`whatsappMeta.list.status.${wabaStatus(waba)}`) }}
                            </UiBadge>
                        </span>
                        <span
                            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500"
                        >
                            <span class="flex items-center">
                                <PhoneIcon
                                    class="mr-1 h-4 w-4 flex-none text-gray-400"
                                    aria-hidden="true"
                                />
                                {{
                                    t(
                                        'whatsappMeta.list.operatingPhoneNumbers',
                                        operatingPhoneCount(waba),
                                    )
                                }}
                            </span>
                            <span class="flex items-center">
                                <UsersIcon
                                    class="mr-1 h-4 w-4 flex-none text-gray-400"
                                    aria-hidden="true"
                                />
                                {{ waba.createdByUser?.name || t('whatsappMeta.list.unknownUser') }}
                            </span>
                            <span v-if="waba.createdAt" class="flex items-center">
                                <CalendarIcon
                                    class="mr-1 h-4 w-4 flex-none text-gray-400"
                                    aria-hidden="true"
                                />
                                <time :datetime="waba.createdAt">
                                    {{ formatDateAsString(waba.createdAt, 'LL') }}
                                </time>
                            </span>
                        </span>
                    </span>
                    <ChevronRightIcon class="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                </button>
            </UiSettingsSection>

            <UiPagination
                v-if="wabas.length"
                v-model="currentPage"
                :count="wabas.length"
                :per-page="perPage"
            />
        </div>
    </div>
</template>
